import React, { useCallback, useState, useRef, useEffect, useContext } from 'react';
import toast from 'react-hot-toast';
import Link from 'next/link';
import Image from 'next/image';
import { LineItem as CartLineItem } from 'shared/types/cart/LineItem';
import QuantityInput, { QuantityInputProps } from 'components/commercetools-ui/organisms/quantity-input';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { RouteHelpers } from 'helpers/routerHelpers';
import { Money } from 'shared/types/product';
import { cn } from 'helpers/utils/classnames';
import { Button } from 'components/ui/button';
import { useCart } from 'frontastic/hooks';
import { gtmQuantitySelector, gtmRemoveFromCart } from 'helpers/utils/gtm-events/gtmEccomerce';
import { LineItemMapper } from 'helpers/utils/gtm-events/mapper/LineItemMapper';
import { useAccount } from 'frontastic/hooks';

interface Props {
  item: CartLineItem;
  className?: string;
  onRemoveItem(): Promise<void>;
  isMiniCart?: boolean;
  isOrderConfirmation?: boolean;
  index?: number;
}

const MAX_QUANTITY = 999;

const CartItem: React.FC<Props> = ({
  item,
  onRemoveItem,
  className,
  isMiniCart = false,
  isOrderConfirmation = false,
  index = 0,
}) => {
  const [quantity, setQuantity] = useState<number>(item.count || 1);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [processing, setProcessing] = useState(false);
  const { account } = useAccount();
  const { updateItem, data: cart } = useCart();
  const handleRemoveItem = useCallback(async () => {
    if (processing) return;

    setProcessing(true);
    await onRemoveItem();
    setProcessing(false);
  }, [onRemoveItem, processing]);

  const increaseQuantity = async () => {
    if (quantity <= MAX_QUANTITY) {
      setQuantity((prev) => prev + 1);
      setIsUpdating(true);
      await updateItem(item?.lineItemId!, quantity + 1);
      setIsUpdating(false);
      toast.success('Item has been updated');
      gtmQuantitySelector({
        clickAction: 'increased',
        itemDataLayer: LineItemMapper.fromLineItemToItemDataLayer(item, quantity + 1, index, cart?.discountCodes),
        newQuantity: quantity + 1,
        source: 'cart',
        cart,
        account,
      });
    }
  };

  const decreaseQuantity = async () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
      setIsUpdating(true);
      await updateItem(item?.lineItemId!, quantity - 1);
      setIsUpdating(false);
      toast.success('Item has been updated');
      gtmQuantitySelector({
        clickAction: 'reduced',
        itemDataLayer: LineItemMapper.fromLineItemToItemDataLayer(item, quantity - 1, index, cart?.discountCodes),
        newQuantity: quantity - 1,
        source: 'cart',
        cart,
        account,
      });
    }
  };

  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

  const productUrl = `${item._url}${RouteHelpers.encodeToURL({ color: item.variant?.attributes?.actualColor, size: item.variant?.attributes?.size })}`;
  const isClearance =
    item?.variant?.originalPrice && item.variant?.price?.centAmount !== item.variant?.originalPrice.centAmount;
  const isDiscontinued = item.variant?.attributes?.isDiscontinued ?? false;
  const isValidQuantityForDiscontinued = isDiscontinued && (item.count || 0) <= (item.variant?.availableQuantity || 0);
  const removeConfirmationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showRemoveConfirmation && removeConfirmationRef.current) {
      removeConfirmationRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [showRemoveConfirmation]);

  return (
    <li
      className={cn(
        'mb-5 mt-3 w-full rounded border px-5 py-6 pb-5 text-sm font-normal text-primary-dark lg:mt-0 lg:py-2',
        className,
      )}
      aria-label={`Cart item: ${item.name}`}
    >
      <section
        className={cn('mt-4 grid grid-rows-1 gap-4 lg:grid-cols-2 lg:gap-12', {
          'lg:grid-cols-[2fr_1fr] lg:gap-6': isMiniCart,
        })}
        aria-labelledby={`product-${item.variant?.sku}`}
      >
        <div className={cn('flex', { 'flex-grow': isMiniCart })}>
          <div className={cn('mr-4 w-[120px] shrink-0', { 'w-20': isMiniCart })}>
            <Link href={productUrl} aria-label={`View details of ${item.name} image`}>
              <Image
                loader={amplienceImageLoader}
                src={item?.variant?.images?.[0] || ''}
                width={120}
                height={120}
                loading="lazy"
                className="rounded-[6px]"
                alt={`A ${item.name} product in cart`}
              />
            </Link>
          </div>
          <div className="ml-2 text-sm font-normal leading-7 text-primary-dark">
            <Link
              href={productUrl}
              className="cursor-pointer text-base font-semibold"
              aria-label={`View details of ${item.name}`}
            >
              {item.name}
            </Link>
            <p>{item.variant?.sku}</p>
            <p>Color: {item.variant?.attributes?.actualColor}</p>
            {item?.variant?.attributes?.filterFinish && (
              <p>
                Finish:
                <span className="ml-1">
                  {[
                    item.variant.attributes?.color ?? '',
                    ...item?.variant?.attributes?.filterFinish.map((finish: { label: string }) => finish?.label),
                  ]
                    .filter((label: string) => !!label)
                    .join(', ')}
                </span>
              </p>
            )}
            {!isOrderConfirmation && <p className="font-semibold">{item?.variant?.availabilityMessage ?? ''}</p>}
            {item.variant?.attributes?.isDiscontinued && (
              <p className="font-semibold text-red-500">
                {isValidQuantityForDiscontinued
                  ? `Item discontinued. You may only order a maximum quantity of ${item.variant.availableQuantity}`
                  : `The item you previously added to your cart is no longer available. The current available quantity is ${item.variant.availableQuantity}`}
              </p>
            )}
            {!isMiniCart && !isOrderConfirmation && (
              <Button
                onClick={handleRemoveItem}
                className="mb-3 mt-2 underline"
                aria-label="Remove product"
                data-testid="remove-product-button"
              >
                Remove
              </Button>
            )}
          </div>
        </div>
        <div className={cn('mt-6 grid grid-cols-3 gap-6 lg:mt-0 lg:gap-10', { 'md:grid-cols-2 md:gap-6': isMiniCart })}>
          {!isMiniCart && <UnitPriceColumn unitPrice={item.variant?.price} />}
          <QuantityColumn
            onBlur={async () => {
              setIsUpdating(true);
              await updateItem(item?.lineItemId!, quantity);
              setIsUpdating(false);
              toast.success('Item has been updated');
            }}
            maxQuantity={isClearance || isDiscontinued ? item.variant?.availableQuantity || 1 : 999}
            isMiniCart={isMiniCart}
            handleRemoveItem={handleRemoveItem}
            onChange={setQuantity}
            onDecreaseQuantity={decreaseQuantity}
            onIncreaseQuantity={increaseQuantity}
            quantity={quantity}
            setShowRemoveConfirmation={setShowRemoveConfirmation}
            disabled={isUpdating}
            isOrderConfirmation={isOrderConfirmation}
          />
          <TotalPriceColumn lineItem={item} isMiniCart={isMiniCart} />
        </div>
      </section>

      {showRemoveConfirmation && (
        <div ref={removeConfirmationRef} className="flex flex-col" role="alert">
          <span className="text-base font-medium text-accent-red">Are you sure you want to remove this product?</span>
          <div className="my-4 flex flex-col gap-4 md:flex-row">
            <button
              onClick={async () => {
                await handleRemoveItem();
                if (cart) {
                  gtmRemoveFromCart(item, quantity, cart, index + 1, cart?.discountCodes, account);
                }
                setShowRemoveConfirmation(false);
              }}
              className="rounded-md bg-primary-gray px-16 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-30"
              aria-label={`Confirm removal of ${item.name}`}
              disabled={processing}
              data-testid="confirm-removal-button"
            >
              Remove
            </button>
            <button
              onClick={() => setShowRemoveConfirmation(false)}
              className="font-semibold underline"
              aria-label="Cancel removal"
              data-testid="cancel-removal-button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

const UnitPriceColumn = ({ unitPrice }: { unitPrice?: Money }) => {
  return (
    <div>
      <p className="text-sm font-semibold text-primary-dark">Unit Price</p>
      <div>
        {unitPrice && <p className="mt-2 text-base font-normal">{CurrencyHelpers.formatForCurrency(unitPrice)}</p>}
      </div>
    </div>
  );
};

const QuantityColumn = ({
  maxQuantity,
  onChange,
  onDecreaseQuantity,
  onIncreaseQuantity,
  quantity,
  isMiniCart,
  disabled,
  onBlur,
  setShowRemoveConfirmation,
  isOrderConfirmation,
}: QuantityInputProps & {
  isOrderConfirmation: boolean;
  isMiniCart: boolean;
  handleRemoveItem: () => Promise<void>;
  setShowRemoveConfirmation: (show: boolean) => void;
}) => {
  return (
    <div>
      <p className="text-sm font-semibold text-primary-dark">Qty</p>
      {isOrderConfirmation ? (
        <p className="ml-2 mt-2 text-base font-normal">{quantity}</p>
      ) : (
        <>
          <div className="mt-2 flex w-[80px] items-center justify-between rounded-lg border">
            <QuantityInput
              maxQuantity={maxQuantity}
              onChange={onChange}
              onDecreaseQuantity={onDecreaseQuantity}
              onIncreaseQuantity={onIncreaseQuantity}
              quantity={quantity}
              disabled={disabled}
              onBlur={onBlur}
            />
          </div>
          {isMiniCart && (
            <Button
              onClick={() => setShowRemoveConfirmation(true)}
              className="mb-3 mt-3 underline"
              aria-label="Remove item"
              data-testid="remove-item-button"
            >
              Remove
            </Button>
          )}
        </>
      )}
    </div>
  );
};

const TotalPriceColumn = ({ lineItem, isMiniCart }: { lineItem: CartLineItem; isMiniCart: boolean }) => {
  const count = lineItem?.count || 1;

  const totalPrice = lineItem?.totalPrice?.centAmount! || 1;

  const unitPrice = lineItem?.price?.centAmount! || 1;

  const originalPrice = lineItem.variant?.originalPrice;

  const totalLineItemPriceWithoutDiscount: Money = {
    centAmount: unitPrice * count,
    currencyCode: 'USD',
    fractionDigits: lineItem?.price?.fractionDigits,
  };

  const isDiscountApplied =
    totalLineItemPriceWithoutDiscount.centAmount !== totalPrice || lineItem.variant?.originalPrice;
  return (
    <div className="text-sm font-semibold">
      {!isMiniCart && <p>Total</p>}
      <div className="flex flex-col gap-2">
        {isDiscountApplied && (
          <strong className="block text-left text-base font-semibold leading-5 text-red-500">
            {CurrencyHelpers.formatForCurrency(lineItem.totalPrice!)}
          </strong>
        )}
        {totalPrice && (
          <>
            {originalPrice ? (
              <p className={cn('mt-2 text-base font-normal line-through')}>
                {CurrencyHelpers.formatForCurrency({
                  ...originalPrice,
                  centAmount: originalPrice.centAmount! * (lineItem.count || 1),
                })}
              </p>
            ) : (
              <p className={cn('mt-2 text-base font-normal', { 'line-through': isDiscountApplied })}>
                {CurrencyHelpers.formatForCurrency(totalLineItemPriceWithoutDiscount)}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CartItem;
